<template>
  <Teleport to="body">
    <Transition name="sidebar">
      <div v-if="open" class="app-sidebar">
        <div class="app-sidebar__overlay" @click="emit('close')" />

        <aside class="app-sidebar__panel" role="dialog" :aria-label="$t('nav.menu')">
          <div class="app-sidebar__header">
            <span class="app-sidebar__title">{{ $t('nav.menu') }}</span>
            <button
              class="app-sidebar__close"
              type="button"
              :aria-label="$t('common.close')"
              @click="emit('close')"
            >
              <span class="app-sidebar__close-icon" :style="{ '--nav-icon': closeIcon }" aria-hidden="true" />
            </button>
          </div>

          <nav class="app-sidebar__nav">
            <RouterLink
              v-for="item in items"
              :key="item.name"
              class="app-sidebar__link"
              :class="{ 'app-sidebar__link--active': isActive(item) }"
              :to="item.route"
              @click="emit('close')"
            >
              <span
                class="app-sidebar__link-icon"
                :style="{ '--nav-icon': cssIconUrl(item.icon ?? '') }"
                aria-hidden="true"
              />
              <span class="app-sidebar__link-label">{{ $t(item.label) }}</span>
            </RouterLink>

            <!-- HK-IGRA.391 — установка кабинета как приложения: та же логика,
                 что у карточки в профиле (usePwaInstallAction). -->
            <template v-if="!isInstalled">
              <a
                v-if="isInTelegram && telegramInstallUrl"
                class="app-sidebar__link app-sidebar__link--install"
                :href="telegramInstallUrl"
                target="_blank"
                rel="noopener noreferrer"
                data-test="sidebar-install"
                @click="emit('close')"
              >
                <span class="app-sidebar__link-icon" :style="{ '--nav-icon': installIcon }" aria-hidden="true" />
                <span class="app-sidebar__link-label">{{ $t('pwa.card.telegramCta') }}</span>
              </a>
              <button
                v-else-if="!isInTelegram"
                type="button"
                class="app-sidebar__link app-sidebar__link--install"
                data-test="sidebar-install"
                @click="installFromMenu"
              >
                <span class="app-sidebar__link-icon" :style="{ '--nav-icon': installIcon }" aria-hidden="true" />
                <span class="app-sidebar__link-label">{{ $t('pwa.title') }}</span>
              </button>
            </template>
          </nav>
        </aside>
      </div>
    </Transition>
  </Teleport>

  <!-- Инструкция живёт вне шторки: шторка к этому моменту уже закрыта. -->
  <PwaInstallModal :visible="guideOpen" @close="guideOpen = false" />
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'

import type { NavigationItem } from '@/shared/types/navigation'
import { cssIconUrl } from '@/shared/utils/assets'
import IconClose from '@/assets/images/icons/sections/close.svg'
import IconInstall from '@/assets/images/icons/sections/install.svg'
import PwaInstallModal from '@/components/common/PwaInstallModal.vue'
import { usePwaInstallAction } from '@/shared/composables/usePwaInstallAction'

// Шторка гамбургера: сюда попадают разделы, которые не влезли в верхнюю
// полоску. Список решает VpnTabNav — шторка только рисует.
defineProps<{
  open: boolean
  items: NavigationItem[]
}>()

const emit = defineEmits<{
  close: []
}>()

const route = useRoute()
// Инлайн, а не v-bind() в стилях: шторка телепортирована в body, и CSS-переменные
// v-bind до неё не доезжают — маска отваливалась в сплошной квадрат.
const closeIcon = cssIconUrl(IconClose)
const installIcon = cssIconUrl(IconInstall)

const { isInTelegram, isInstalled, telegramInstallUrl, guideOpen, install } = usePwaInstallAction()

function installFromMenu(): void {
  emit('close')
  void install()
}

const isActive = (item: NavigationItem) => route.path.startsWith(item.route)
</script>

<style scoped lang="scss">
.app-sidebar {
  position: fixed;
  inset: 0;
  z-index: 150;

  &__overlay {
    position: absolute;
    inset: 0;
    background: var(--ds-scrim);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  &__panel {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    max-width: 85vw;
    background: var(--ds-surface);
    border-left: 1px solid var(--ds-border);
    box-shadow: -8px 0 32px var(--ds-scrim);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid var(--ds-border);
  }

  &__title {
    font-size: var(--ds-text-size-body-lg);
    font-weight: var(--ds-weight-semibold);
    color: var(--ds-text);
  }

  &__close {
    width: 36px;
    height: 36px;
    border-radius: var(--p-radius-50);
    border: none;
    background: var(--ds-accent-soft-08);
    color: var(--ds-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    flex-shrink: 0;

    &:hover {
      background: var(--ds-accent-soft-12);
      color: var(--ds-accent);
    }
  }

  &__close-icon {
    width: 18px;
    height: 18px;
    background-color: currentColor;
    mask: var(--nav-icon) no-repeat center / contain;
    -webkit-mask: var(--nav-icon) no-repeat center / contain;
  }

  &__nav {
    display: flex;
    flex-direction: column;
    padding: 12px;
    gap: 4px;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border-radius: var(--ds-radius-control);
    color: var(--ds-text-secondary);
    text-decoration: none;
    font-size: var(--p-text-15);
    font-weight: var(--ds-weight-medium);
    transition: background 0.15s ease, color 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    @media (hover: hover) {
      &:hover:not(&--active) {
        background: var(--ds-accent-soft-08);
        color: var(--ds-text);
      }
    }
  }

  // Пункт установки отделён от разделов и оформлен как кнопка, а не ссылка.
  &__link--install {
    margin-top: 8px;
    padding-top: 18px;
    border: none;
    border-top: 1px solid var(--ds-border);
    border-radius: 0;
    background: transparent;
    width: 100%;
    font: inherit;
    cursor: pointer;
    text-align: left;
  }

  &__link--active {
    background: var(--ds-accent-soft-12);
    color: var(--ds-accent);
    font-weight: var(--ds-weight-semibold);
  }

  &__link-icon {
    flex: none;
    width: 22px;
    height: 22px;
    background-color: currentColor;
    mask: var(--nav-icon) no-repeat center / contain;
    -webkit-mask: var(--nav-icon) no-repeat center / contain;
  }

  &__link-label {
    flex: 1;
  }
}

.sidebar-enter-active,
.sidebar-leave-active {
  transition: opacity 0.25s ease;

  .app-sidebar__panel {
    transition: transform 0.25s ease;
  }
}

.sidebar-enter-from,
.sidebar-leave-to {
  opacity: 0;

  .app-sidebar__panel {
    transform: translateX(100%);
  }
}
</style>
